import { PredefinedRoutine, PredefinedRoutineTask } from '../services/supabase/routines';

export interface ProposedTask {
    label: string;
    icon: string;
    color: string;
    duration: number; // minutes
    orderIndex: number;
}

export const timeToMinutes = (time: string): number => {
    if (!time) return 0;
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
};

export const minutesToTime = (minutes: number): string => {
    const h = Math.floor(minutes / 60);
    const m = Math.floor(minutes % 60);
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
};

export const addMinutes = (time: string, minutes: number): string => {
    const total = timeToMinutes(time) + minutes;
    // Handle wrap around 24h if needed, but for routines usually within day
    const normalized = (total + 24 * 60) % (24 * 60);
    return minutesToTime(normalized);
};

export const generateTasksFromTemplate = (
    template: PredefinedRoutine & { tasks: PredefinedRoutineTask[] },
    targetDurationMinutes: number
): ProposedTask[] => {
    if (!template.tasks || template.tasks.length === 0) return [];
    
    // Determine scale factor
    const defaultTotal = template.tasks.reduce((sum, t) => sum + t.default_duration_minutes, 0);
    
    // Avoid division by zero
    const scale = defaultTotal > 0 ? targetDurationMinutes / defaultTotal : 1;
    
    let currentTotal = 0;
    // Map to mutable objects
    const tasks: ProposedTask[] = template.tasks
        .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
        .map(t => {
            const scaled = Math.round(t.default_duration_minutes * scale);
            const duration = Math.max(3, scaled); 
            currentTotal += duration;
            return {
                label: t.label,
                icon: t.icon || '📝',
                color: t.color || '#5DADE2',
                duration: duration,
                orderIndex: t.order_index ?? 0
            };
        });
        
    // Adjust total to match exactly targetDurationMinutes
    let diff = targetDurationMinutes - currentTotal;
    
    if (diff !== 0 && tasks.length > 0) {
        let remainingDiff = diff;
        let safetyCounter = 0;
        
        while (remainingDiff !== 0 && safetyCounter < 100) {
            safetyCounter++;
            if (remainingDiff > 0) {
                // Add to largest task
                const maxTask = tasks.reduce((prev, current) => (prev.duration > current.duration) ? prev : current);
                maxTask.duration += 1;
                remainingDiff -= 1;
            } else {
                // Remove from largest task that is > 3
                const validTasks = tasks.filter(t => t.duration > 3);
                if (validTasks.length === 0) break; // Cannot reduce further
                
                const maxTask = validTasks.reduce((prev, current) => (prev.duration > current.duration) ? prev : current);
                maxTask.duration -= 1;
                remainingDiff += 1;
            }
        }
    }
    
    return tasks;
};

export const redistributeTaskDurations = (
    tasks: ProposedTask[],
    targetDurationMinutes: number
): ProposedTask[] => {
    // This function creates a NEW array with redistributed times
    if (tasks.length === 0) return [];

    let currentTotal = tasks.reduce((sum, t) => sum + t.duration, 0);
    
    // If current total is 0 (e.g. all new tasks), distribute evenly
    if (currentTotal === 0) {
        const split = Math.floor(targetDurationMinutes / tasks.length);
        const remainder = targetDurationMinutes % tasks.length;
        return tasks.map((t, i) => ({
            ...t,
            duration: split + (i < remainder ? 1 : 0)
        }));
    }
    
    const scale = targetDurationMinutes / currentTotal;
    
    let newTotal = 0;
    const newTasks = tasks.map(t => {
        const d = Math.max(3, Math.round(t.duration * scale));
        newTotal += d;
        return { ...t, duration: d };
    });
    
    // Adjust remainder
    let diff = targetDurationMinutes - newTotal;
    let safetyCounter = 0;

    while (diff !== 0 && safetyCounter < 100) {
        safetyCounter++;
        if (diff > 0) {
            const maxTask = newTasks.reduce((prev, current) => (prev.duration > current.duration) ? prev : current);
            maxTask.duration += 1;
            diff -= 1;
        } else {
             const validTasks = newTasks.filter(t => t.duration > 3);
            if (validTasks.length === 0) break;
            const maxTask = validTasks.reduce((prev, current) => (prev.duration > current.duration) ? prev : current);
            maxTask.duration -= 1;
            diff += 1;
        }
    }
    
    return newTasks;
};
